import { useEffect, useMemo, useState } from 'react'
import type { AgreementInfo } from '@zetyar/domain'
import { Edit2, Globe, Handshake, Phone, Plus, Trash2 } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webSupplyRepository } from '@/infrastructure/repositories/supplies'
import { Button } from '@/presentation/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'

interface AgreementFormState {
  readonly partnerName: string
  readonly title: string
  readonly description: string
  readonly benefitInfo: string
  readonly contactPhone: string
  readonly websiteUrl: string
}

const emptyForm: AgreementFormState = {
  partnerName: '',
  title: '',
  description: '',
  benefitInfo: '',
  contactPhone: '',
  websiteUrl: '',
}

function displayBenefit(discountDetails: string | null): string | null {
  if (!discountDetails) return null

  try {
    const parsed = JSON.parse(discountDetails) as { benefitInfo?: unknown }
    return typeof parsed.benefitInfo === 'string' ? parsed.benefitInfo : null
  } catch {
    return discountDetails
  }
}

export function AgreementsPage() {
  const { currentMembership, currentOrganization, permissions, profile, user } = useApp()
  const [agreements, setAgreements] = useState<AgreementInfo[]>([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [editingAgreement, setEditingAgreement] = useState<AgreementInfo | null>(null)
  const [form, setForm] = useState<AgreementFormState>(emptyForm)
  const [isSaving, setIsSaving] = useState(false)

  const canManage = profile?.isSuperAdmin || permissions.includes('supplies.manage')

  const tenantContext = () => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }

  const filteredAgreements = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return agreements

    return agreements.filter((agreement) =>
      [agreement.partnerName, agreement.title, agreement.description, displayBenefit(agreement.discountDetails) ?? '']
        .join(' ')
        .toLowerCase()
        .includes(query),
    )
  }, [agreements, search])

  const load = async () => {
    const context = tenantContext()
    if (!context || !webSupplyRepository.listAgreementInfos) {
      setAgreements([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setAgreements(await webSupplyRepository.listAgreementInfos(context, 200))
    } catch (error) {
      console.error('Error al cargar convenios:', error)
      setAgreements([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership, currentOrganization, profile, user])

  const openCreate = () => {
    setEditingAgreement(null)
    setForm(emptyForm)
    setIsEditorOpen(true)
  }

  const openEdit = (agreement: AgreementInfo) => {
    setEditingAgreement(agreement)
    setForm({
      partnerName: agreement.partnerName,
      title: agreement.title,
      description: agreement.description,
      benefitInfo: displayBenefit(agreement.discountDetails) ?? '',
      contactPhone: agreement.contactPhone ?? '',
      websiteUrl: agreement.websiteUrl ?? '',
    })
    setIsEditorOpen(true)
  }

  const saveAgreement = async (event: React.FormEvent) => {
    event.preventDefault()
    const context = tenantContext()
    if (!context) return

    const payload = {
      partnerName: form.partnerName.trim(),
      title: form.title.trim(),
      description: form.description.trim(),
      discountDetails: form.benefitInfo.trim() || null,
      contactPhone: form.contactPhone.trim() || null,
      websiteUrl: form.websiteUrl.trim() || null,
    }

    if (!payload.partnerName || !payload.title || !payload.description) return

    try {
      setIsSaving(true)
      if (editingAgreement) {
        await webSupplyRepository.updateAgreementInfo?.(context, editingAgreement.id, payload)
      } else {
        await webSupplyRepository.createAgreementInfo?.(context, payload)
      }
      setIsEditorOpen(false)
      setEditingAgreement(null)
      setForm(emptyForm)
      await load()
    } finally {
      setIsSaving(false)
    }
  }

  const deleteAgreement = async (agreementId: string) => {
    const context = tenantContext()
    if (!context || !webSupplyRepository.deleteAgreementInfo) return
    if (!window.confirm('Eliminar este convenio?')) return
    await webSupplyRepository.deleteAgreementInfo(context, agreementId)
    await load()
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Convenios</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{currentOrganization?.name ?? ''}</p>
        </div>
        {canManage && (
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
            Nuevo convenio
          </Button>
        )}
      </div>

      <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar convenio" />

      {isLoading ? (
        <Card><CardContent className="py-10 text-center text-sm text-neutral-500">Cargando convenios...</CardContent></Card>
      ) : filteredAgreements.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-neutral-500">No hay convenios registrados.</CardContent></Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {filteredAgreements.map((agreement) => {
            const benefit = displayBenefit(agreement.discountDetails)
            return (
              <Card key={agreement.id}>
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle className="truncate">{agreement.partnerName}</CardTitle>
                    <p className="mt-1 text-sm font-medium text-neutral-700 dark:text-neutral-200">{agreement.title}</p>
                  </div>
                  <Handshake className="h-5 w-5 text-primary-600" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-neutral-600 dark:text-neutral-300">{agreement.description}</p>
                  {benefit && <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{benefit}</p>}
                  <div className="flex flex-wrap gap-3 text-sm text-neutral-500 dark:text-neutral-400">
                    {agreement.contactPhone && <span className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4" />{agreement.contactPhone}</span>}
                    {agreement.websiteUrl && (
                      <a className="inline-flex items-center gap-1.5 text-primary-600" href={agreement.websiteUrl.startsWith('http') ? agreement.websiteUrl : `https://${agreement.websiteUrl}`} target="_blank" rel="noreferrer">
                        <Globe className="h-4 w-4" />Sitio web
                      </a>
                    )}
                  </div>
                  {canManage && (
                    <div className="flex justify-end gap-2 border-t border-neutral-100 pt-4 dark:border-neutral-800">
                      <Button variant="outline" size="sm" leftIcon={<Edit2 className="h-4 w-4" />} onClick={() => openEdit(agreement)}>Editar</Button>
                      <Button variant="danger" size="sm" leftIcon={<Trash2 className="h-4 w-4" />} onClick={() => void deleteAgreement(agreement.id)}>Eliminar</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <Modal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        title={editingAgreement ? 'Editar convenio' : 'Nuevo convenio'}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setIsEditorOpen(false)}>Cancelar</Button>
            <Button form="agreement-form" type="submit" isLoading={isSaving}>Guardar</Button>
          </>
        }
      >
        <form id="agreement-form" className="grid gap-4 sm:grid-cols-2" onSubmit={saveAgreement}>
          <Input label="Entidad" value={form.partnerName} onChange={(event) => setForm({ ...form, partnerName: event.target.value })} required />
          <Input label="Titulo" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} required />
          <Input label="Descripcion" className="sm:col-span-2" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} required />
          <Input label="Beneficio" className="sm:col-span-2" value={form.benefitInfo} onChange={(event) => setForm({ ...form, benefitInfo: event.target.value })} />
          <Input label="Telefono" value={form.contactPhone} onChange={(event) => setForm({ ...form, contactPhone: event.target.value })} />
          <Input label="Sitio web" value={form.websiteUrl} onChange={(event) => setForm({ ...form, websiteUrl: event.target.value })} />
        </form>
      </Modal>
    </div>
  )
}